import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import { DatePicker } from '../../lib/DatePicker'
import { dowText } from '../../lib/person'
import { employeesApi, type EmployeeRow } from '../../lib/employees'
import { errorText } from '../../lib/offsite'
import { useAuthedSession } from '../../lib/SessionContext'

/* ============================================================================
   แก้กะรายวัน · backend: src/routes/adminShiftPlan.js (/day)

   ต่างจาก "สร้างตารางกะ" (/admin/shift-plan) ที่เขียนเป็นช่วง — หน้านี้แก้
   "วันเดียว" ของคนเดียว ใช้ตอนสลับกะเฉพาะวัน (เข้าแทนเพื่อน/สลับเวร)
   โดยไม่กระทบวันอื่น

   ⚠️ เขียน HR_EMP_WORK ของ production — ถ้าวันนั้นมีเวลาตอกบัตรแล้ว backend จะ
      สั่งคำนวณเวลาทำงานใหม่ให้อัตโนมัติ (usp_RecalcAttendance)
   ============================================================================ */

const BASE = '/api/admin/shift-plan'

type ShiftOption = {
  Shift_ID: number
  Shift_Desc: string
  Shift_Start: string | null
  Shift_End: string | null
}

type Day = {
  date: string
  shiftName: string | null
  isHoliday: boolean
  shiftStart: string | null
  shiftEnd: string | null
  hasLeave: boolean
  hasPunch: boolean
  processTa: boolean
}

const p2 = (n: number) => String(n).padStart(2, '0')
const firstOfMonth = (d: Date) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-01`
const lastOfMonth = (d: Date) =>
  `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate())}`

/** '2026-08-15' → 'ส. 15 ส.ค.' (วันในสัปดาห์ + วันที่ไทย) */
function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dow = dowText(new Date(Date.UTC(y, m - 1, d)).getUTCDay())
  const MONTH_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
  return `${dow} ${d} ${MONTH_SHORT[m - 1]}`
}

export default function ShiftDay() {
  const session = useAuthedSession()
  const mayWrite =
    session.kind === 'admin' && (session.role === 'superadmin' || session.role === 'admin')

  const today = new Date()
  const [shifts, setShifts] = useState<ShiftOption[]>([])
  const [minDate, setMinDate] = useState('')
  const [emp, setEmp] = useState<EmployeeRow | null>(null)
  const [from, setFrom] = useState(firstOfMonth(today))
  const [to, setTo] = useState(lastOfMonth(today))

  const [days, setDays] = useState<Day[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /* วันที่กำลังแก้ (กันกดซ้ำระหว่างบันทึก) */
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    api<{ shifts: ShiftOption[]; minDate: string }>(`${BASE}/options`)
      .then((d) => {
        setShifts(d.shifts)
        setMinDate(d.minDate)
      })
      .catch((e: unknown) => setError(errorText(e)))
  }, [])

  const load = useCallback(async () => {
    if (!emp || !from || !to) {
      setDays(null)
      return
    }
    setLoading(true)
    try {
      const d = await api<{ days: Day[] }>(
        `${BASE}/day?empCode=${encodeURIComponent(emp.empCode)}&from=${from}&to=${to}`,
      )
      setDays(d.days)
      setError(null)
    } catch (e: unknown) {
      setError(errorText(e))
      setDays(null)
    } finally {
      setLoading(false)
    }
  }, [emp, from, to])

  useEffect(() => {
    void load()
  }, [load])

  async function changeDay(date: string, shiftId: number | null) {
    setSaving(date)
    setNotice(null)
    setError(null)
    try {
      const r = await api<{ shiftName: string | null; recalculated: boolean }>(`${BASE}/day`, {
        method: 'PATCH',
        body: JSON.stringify({ empCode: emp?.empCode, date, shiftId }),
      })
      setNotice(
        `แก้กะ ${dayLabel(date)} → ${r.shiftName ?? 'วันหยุด'} แล้ว` +
          (r.recalculated ? ' · คำนวณเวลาทำงานใหม่ให้แล้ว' : ''),
      )
      await load()
    } catch (e: unknown) {
      setError(errorText(e))
    } finally {
      setSaving(null)
    }
  }

  return (
    <>
      <h1>แก้กะรายวัน</h1>
      <p className="meta">
        เปลี่ยนกะของพนักงานทีละวัน (สลับเวร / เข้าแทน) โดยไม่กระทบวันอื่น —
        ถ้าต้องตั้งกะเป็นช่วงยาวใช้หน้า <strong>สร้างตารางกะ</strong> แทน
      </p>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      <section className="card">
        <EmployeePicker picked={emp} onPick={setEmp} />
      </section>

      {emp && (
        <section className="card">
          <div className="toolbar">
            <label>
              ตั้งแต่
              <DatePicker value={from} onChange={setFrom} min={minDate} />
            </label>
            <label>
              ถึง
              <DatePicker value={to} onChange={setTo} min={from || minDate} />
            </label>
            <button type="button" className="ghost" onClick={() => void load()} disabled={loading}>
              {loading ? 'กำลังโหลด…' : 'โหลดใหม่'}
            </button>
          </div>

          {!mayWrite && (
            <p className="warn">บัญชีนี้มีสิทธิ์ดูอย่างเดียว — เปลี่ยนกะไม่ได้</p>
          )}

          {days === null ? (
            <p className="meta">กำลังโหลด…</p>
          ) : days.length === 0 ? (
            <p className="meta">ไม่มีตารางกะในช่วงนี้ — ใช้หน้า "สร้างตารางกะ" สร้างก่อน</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>วันที่</th>
                    <th>กะปัจจุบัน</th>
                    <th>เวลา</th>
                    <th>สถานะ</th>
                    {mayWrite && <th>เปลี่ยนเป็น</th>}
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date} className={d.isHoliday ? 'off' : undefined}>
                      <td className="nowrap">{dayLabel(d.date)}</td>
                      <td>
                        {d.isHoliday ? (
                          <span className="chip chip--off">วันหยุด</span>
                        ) : (
                          (d.shiftName ?? <span className="meta">—</span>)
                        )}
                      </td>
                      <td className="nowrap meta">
                        {d.shiftStart && d.shiftEnd ? `${d.shiftStart}–${d.shiftEnd}` : '—'}
                      </td>
                      <td className="nowrap">
                        {d.hasLeave && <span className="chip chip--warn">มีใบลา</span>}
                        {d.hasPunch && <span className="meta"> ตอกบัตรแล้ว</span>}
                      </td>
                      {mayWrite && (
                        <td className="nowrap">
                          {d.hasLeave ? (
                            <span className="meta">ยกเลิกใบลาก่อน</span>
                          ) : (
                            <select
                              value=""
                              disabled={saving === d.date}
                              onChange={(e) => {
                                const v = e.target.value
                                if (v === '') return
                                void changeDay(d.date, v === '__off__' ? null : Number(v))
                              }}
                            >
                              <option value="">{saving === d.date ? 'กำลังบันทึก…' : '— เลือกกะ —'}</option>
                              <option value="__off__">วันหยุด (OFF)</option>
                              {shifts.map((s) => (
                                <option key={s.Shift_ID} value={s.Shift_ID}>
                                  {s.Shift_Desc}
                                  {s.Shift_Start && s.Shift_End ? ` (${s.Shift_Start}–${s.Shift_End})` : ''}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </>
  )
}

/** เลือกพนักงาน 1 คน — ค้นแล้วกดเลือก */
function EmployeePicker({
  picked,
  onPick,
}: {
  picked: EmployeeRow | null
  onPick: (row: EmployeeRow | null) => void
}) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<EmployeeRow[] | null>(null)
  const [loading, setLoading] = useState(false)

  const search = useCallback(async (text: string) => {
    if (text.trim() === '') {
      setRows(null)
      return
    }
    setLoading(true)
    try {
      const d = await employeesApi(api).list({ q: text, status: 'active', pageSize: 20 })
      setRows(d.employees)
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => void search(q), 300)
    return () => clearTimeout(t)
  }, [q, search])

  if (picked) {
    return (
      <p className="chips">
        <span className="meta">พนักงาน: </span>
        <button type="button" className="chip chip--pick" onClick={() => onPick(null)} title="เปลี่ยนคน">
          {picked.empCode} {picked.fullName} ✕
        </button>
      </p>
    )
  }

  return (
    <>
      <p className="fieldset-title">เลือกพนักงาน</p>
      <div className="emp-toolbar">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ค้นรหัสหรือชื่อพนักงาน"
          aria-label="ค้นหาพนักงาน"
        />
      </div>
      {loading ? (
        <p className="meta">กำลังค้น…</p>
      ) : rows && rows.length === 0 ? (
        <p className="meta">ไม่พบพนักงาน</p>
      ) : rows ? (
        <div className="table-wrap table-wrap--rows10">
          <table className="data">
            <thead>
              <tr>
                <th>รหัส</th>
                <th>ชื่อ</th>
                <th>แผนก</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.empCode}>
                  <td className="nowrap">{r.empCode}</td>
                  <td className="cell-clip" title={r.fullName ?? ''}>
                    {r.fullName}
                  </td>
                  <td className="cell-clip" title={r.departName ?? ''}>
                    {r.departName}
                  </td>
                  <td>
                    <button type="button" className="ghost tiny" onClick={() => onPick(r)}>
                      เลือก
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="meta">พิมพ์เพื่อค้นหาพนักงาน</p>
      )}
    </>
  )
}
